import { mapValues, sum, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid, getBundlesByTruckloadGuid } from './bundles';

export const getAggregatedTrussStatisticsByTruckloadGuid = createSelector(
  (state: RootState) => state.truckloads,
  getBundlesByTruckloadGuid,
  getAggregatedTrussStatisticsByBundleGuid,

  (
    truckloads,
    bundlesByTruckloadGuid,
    aggregatedTrussStatisticsByBundleGuid,
  ): { [scheduleGroupGuid: string]: AggregatedTrussStatistics } =>
    mapValues(truckloads, (truckload) => {
      // Do not show values unless all are loaded - we do not want to provide misleading information
      const allTrussesForTruckloadAreLoaded = bundlesByTruckloadGuid[truckload.guid].every(
        (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses.length > 0,
      );
      if (!allTrussesForTruckloadAreLoaded)
        return {
          boardFeet: null,
          linearFeet: null,
          trussCount: sum(
            bundlesByTruckloadGuid[truckload.guid]?.flatMap(
              (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
            ),
          ),
          thicknesses: [],
          widths: [],
          heights: [],
        };

      return {
        boardFeet: sum(
          bundlesByTruckloadGuid[truckload.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].boardFeet ?? 0,
          ),
        ),
        linearFeet: sum(
          bundlesByTruckloadGuid[truckload.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].linearFeet ?? 0,
          ),
        ),
        trussCount: sum(
          bundlesByTruckloadGuid[truckload.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
          ),
        ),
        heights: uniqWith(
          bundlesByTruckloadGuid[truckload.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].heights)
            .filter(notNullish),
          distanceEquals,
        ),
        widths: uniqWith(
          bundlesByTruckloadGuid[truckload.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].widths)
            .filter(notNullish),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          bundlesByTruckloadGuid[truckload.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses)
            .filter(notNullish),
          distanceEquals,
        ),
      };
    }),
);
